const accessTokenKey = "void_chat_access_token";
const refreshTokenKey = "void_chat_refresh_token";

type SessionTokens = {
  accessToken: string;
  refreshToken?: string;
};

type RefreshResponse = {
  data?: SessionTokens;
  message?: string;
};

let refreshInFlight: Promise<SessionTokens> | null = null;
let lastRefreshResult: { tokens: SessionTokens; refreshedAt: number } | null = null;

const recentRefreshReuseWindowMs = 5000;

const canUseSessionStorage = () => typeof window !== "undefined" && Boolean( window.sessionStorage );

const voidAuthBaseURL = () => process.env.REACT_APP_VOID_AUTH_API_URL?.replace(/\/+$/, "") ?? "";

export const getSessionAccessToken = (): string | undefined => {
  if( !canUseSessionStorage()) return undefined;

  return window.sessionStorage.getItem( accessTokenKey ) ?? undefined;
};

export const getSessionRefreshToken = (): string | undefined => {
  if( !canUseSessionStorage()) return undefined;

  return window.sessionStorage.getItem( refreshTokenKey ) ?? undefined;
};

export const setSessionTokens = ( accessToken?: string, refreshToken?: string ) => {
  setSessionAccessToken( accessToken );

  if( !canUseSessionStorage()) return;

  const trimmed = refreshToken?.trim();
  if( trimmed ) {
    window.sessionStorage.setItem( refreshTokenKey, trimmed );
    return;
  }

  window.sessionStorage.removeItem( refreshTokenKey );
};

export const setSessionAccessToken = ( token?: string ) => {
  if( !canUseSessionStorage()) return;

  const trimmed = token?.trim();
  if( trimmed ) {
    window.sessionStorage.setItem( accessTokenKey, trimmed );
    return;
  }

  window.sessionStorage.removeItem( accessTokenKey );
};

export const clearSessionAccessToken = () => setSessionAccessToken();

export const clearSessionTokens = () => {
  lastRefreshResult = null;
  setSessionAccessToken();
  if( canUseSessionStorage()) window.sessionStorage.removeItem( refreshTokenKey );
};

export const refreshSessionTokens = async (): Promise<SessionTokens> => {
  if( refreshInFlight ) return refreshInFlight;

  const authBaseURL = voidAuthBaseURL();
  const refreshToken = getSessionRefreshToken();

  if(
    lastRefreshResult &&
    Date.now() - lastRefreshResult.refreshedAt < recentRefreshReuseWindowMs &&
    getSessionAccessToken() === lastRefreshResult.tokens.accessToken &&
    (!lastRefreshResult.tokens.refreshToken || refreshToken === lastRefreshResult.tokens.refreshToken)
  ) {
    return lastRefreshResult.tokens;
  }

  if( !authBaseURL || !refreshToken ) {
    clearSessionTokens();
    throw new Error("Refresh token missing");
  }

  refreshInFlight = fetch(`${ authBaseURL }/v1/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  })
    .then(async ( response ) => {
      const payload = await response.json().catch(() => null) as RefreshResponse | null;
      if( !response.ok || !payload?.data?.accessToken ) {
        throw new Error( payload?.message || "Falha ao renovar sessao" );
      }

      setSessionTokens( payload.data.accessToken, payload.data.refreshToken );
      lastRefreshResult = { tokens: payload.data, refreshedAt: Date.now() };
      return payload.data;
    })
    .catch(( error ) => {
      clearSessionTokens();
      throw error;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};
